import { useState, useEffect, useCallback } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Pagination } from "@/components/ui/pagination";
import { EmptyState } from "@/components/ui/empty-state";
import {
  InvoiceStatusBadge,
  invoiceTitle,
  tenantInvoiceAmount,
  CURRENCY_SYMBOL,
} from "@/components/invoice/InvoiceHelpers";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { invoicesApi, type Invoice } from "@/lib/api/invoices";
import {
  Loader2,
  CreditCard,
  Receipt,
  RefreshCw,
  Copy,
  X,
  CheckCircle2,
  Clock,
  FileText,
} from "lucide-react";
import { useToast } from "@/contexts/ToastContext";

const PAGE_SIZE = 20;

type VerifyResult = {
  invoiceId: string;
  status: string;
  message: string;
  reference?: string;
};

export function InvoicesPage() {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState("outstanding");
  const [payingId, setPayingId] = useState<string | null>(null);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [verificationResult, setVerificationResult] = useState<VerifyResult | null>(null);

  // Outstanding state
  const [pendingInvoices, setPendingInvoices] = useState<Invoice[]>([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [pendingReqCursor, setPendingReqCursor] = useState<string | null>(null);
  const [pendingStack, setPendingStack] = useState<(string | null)[]>([]);
  const [pendingNext, setPendingNext] = useState<string | null>(null);
  const [pendingHasMore, setPendingHasMore] = useState(false);

  // Paid state
  const [paidInvoices, setPaidInvoices] = useState<Invoice[]>([]);
  const [paidTotal, setPaidTotal] = useState(0);
  const [paidLoading, setPaidLoading] = useState(true);
  const [paidReqCursor, setPaidReqCursor] = useState<string | null>(null);
  const [paidStack, setPaidStack] = useState<(string | null)[]>([]);
  const [paidNext, setPaidNext] = useState<string | null>(null);
  const [paidHasMore, setPaidHasMore] = useState(false);

  const loadPending = useCallback(async (cursorArg: string | null) => {
    try {
      setPendingLoading(true);
      const res = await invoicesApi.list({
        status: "pending",
        limit: PAGE_SIZE,
        cursor: cursorArg ?? undefined,
      });
      setPendingInvoices(res.invoices);
      setPendingTotal(res.total);
      setPendingNext(res.nextCursor);
      setPendingHasMore(res.hasMore);
      setPendingReqCursor(cursorArg);
    } catch (error) {
      console.error("Failed to load invoices:", error);
    } finally {
      setPendingLoading(false);
    }
  }, []);

  const loadPaid = useCallback(async (cursorArg: string | null) => {
    try {
      setPaidLoading(true);
      const res = await invoicesApi.list({
        status: "paid",
        limit: PAGE_SIZE,
        cursor: cursorArg ?? undefined,
      });
      setPaidInvoices(res.invoices);
      setPaidTotal(res.total);
      setPaidNext(res.nextCursor);
      setPaidHasMore(res.hasMore);
      setPaidReqCursor(cursorArg);
    } catch (error) {
      console.error("Failed to load invoices:", error);
    } finally {
      setPaidLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPending(null);
    void loadPaid(null);
  }, [loadPending, loadPaid]);

  const pendingPage = pendingStack.length + 1;
  const pendingTotalPages = Math.max(1, Math.ceil(pendingTotal / PAGE_SIZE));
  const paidPage = paidStack.length + 1;
  const paidTotalPages = Math.max(1, Math.ceil(paidTotal / PAGE_SIZE));

  const handlePay = async (invoice: Invoice) => {
    try {
      setPayingId(invoice.id);
      const { authorizationUrl } = await invoicesApi.initiatePayment(invoice.id);
      window.location.href = authorizationUrl;
    } catch (error: any) {
      setPayingId(null);
      toast.error(error.response?.data?.message || "Failed to initiate payment");
    }
  };

  const handleVerifyPayment = async (invoice: Invoice) => {
    try {
      setVerifyingId(invoice.id);
      setVerificationResult(null);
      const result = await invoicesApi.verifyPayment(invoice.id);
      if (result.success) {
        toast.success(result.message);
        setVerificationResult(null);
        setPendingStack([]);
        setPaidStack([]);
        void loadPending(null);
        void loadPaid(null);
      } else {
        setVerificationResult({
          invoiceId: invoice.id,
          status: result.status ?? "unknown",
          message: result.message,
          reference: result.reference,
        });
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Verification failed");
    } finally {
      setVerifyingId(null);
    }
  };

  const handlePrintReceipt = (invoiceId: string) => {
    window.open(`/invoices/${invoiceId}/receipt`, "_blank", "noopener,noreferrer");
  };

  const initialLoading =
    pendingLoading && paidLoading && pendingInvoices.length === 0 && paidInvoices.length === 0;

  if (initialLoading) {
    return (
      <div
        className="flex min-h-[40vh] items-center justify-center py-12"
        role="status"
        aria-live="polite"
      >
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" aria-hidden />
        <span className="sr-only">Loading invoices</span>
      </div>
    );
  }

  return (
    <PageContainer width="wide">
      <PageHeader
        title="My Invoices"
        description="Outstanding charges and your payment history"
        breadcrumbItems={[{ label: "Dashboard", href: "/dashboard" }, { label: "My Invoices" }]}
        showSearch={false}
        titleSize="md"
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full sm:w-auto">
          <TabsTrigger
            value="outstanding"
            className="flex-1 gap-1.5 touch-manipulation sm:flex-initial sm:px-4"
          >
            Outstanding
            {pendingTotal > 0 && (
              <Badge className="ml-1 h-5 min-w-[1.25rem] border-amber-200 bg-amber-50 px-1.5 text-[11px] font-semibold text-slate-900 shadow-none hover:bg-amber-50">
                {pendingTotal}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="history"
            className="flex-1 gap-1.5 touch-manipulation sm:flex-initial sm:px-4"
          >
            Payment History
            {paidTotal > 0 && <span className="ml-1 text-xs text-slate-500">({paidTotal})</span>}
          </TabsTrigger>
        </TabsList>

        {/* ── Outstanding tab ──────────────────────────────────── */}
        <TabsContent value="outstanding" className="mt-4 space-y-4">
          {pendingLoading && pendingInvoices.length === 0 ? (
            <div className="flex min-h-[120px] items-center justify-center py-8" role="status">
              <Loader2 className="h-7 w-7 animate-spin text-slate-400" aria-hidden />
            </div>
          ) : pendingInvoices.length === 0 ? (
            <EmptyState
              icon={<CheckCircle2 className="h-6 w-6 text-emerald-500" />}
              title="No outstanding invoices"
              description="You're all caught up! Any new charges will appear here."
            />
          ) : (
            <>
              {/* Desktop / tablet table */}
              <div className="hidden overflow-x-auto rounded-lg border border-slate-200 sm:block">
                <table className="w-full min-w-[640px] border-collapse text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50/80">
                      <th className="w-12 px-3 py-3 text-center font-heading text-xs font-semibold uppercase tracking-wide text-slate-500">
                        S/N
                      </th>
                      <th className="px-4 py-3 font-heading text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Invoice
                      </th>
                      <th className="px-4 py-3 text-right font-heading text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Amount
                      </th>
                      <th className="px-4 py-3 font-heading text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Status
                      </th>
                      <th className="px-4 py-3 font-heading text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Due
                      </th>
                      <th className="px-4 py-3 text-right font-heading text-xs font-semibold uppercase tracking-wide text-slate-500">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {pendingInvoices.map((inv, idx) => {
                      const sn = (pendingPage - 1) * PAGE_SIZE + idx + 1;
                      return (
                        <tr key={inv.id} className="hover:bg-slate-50/60">
                          <td className="whitespace-nowrap px-3 py-3 text-center tabular-nums text-slate-400">
                            {sn}
                          </td>
                          <td className="max-w-[240px] px-4 py-3 align-top">
                            <span className="font-medium text-slate-900">{invoiceTitle(inv)}</span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-right font-medium tabular-nums text-slate-900">
                            {CURRENCY_SYMBOL}
                            {tenantInvoiceAmount(inv).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <InvoiceStatusBadge status={inv.status} />
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-slate-500">
                            {inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : "—"}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex flex-wrap justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="touch-manipulation"
                                onClick={() => handleVerifyPayment(inv)}
                                disabled={verifyingId === inv.id}
                              >
                                {verifyingId === inv.id ? (
                                  <Loader2
                                    className="mr-1.5 h-3.5 w-3.5 animate-spin"
                                    aria-hidden
                                  />
                                ) : (
                                  <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                                )}
                                Verify
                              </Button>
                              <Button
                                size="sm"
                                className="touch-manipulation"
                                onClick={() => handlePay(inv)}
                                disabled={payingId === inv.id}
                              >
                                {payingId === inv.id ? (
                                  <Loader2
                                    className="mr-1.5 h-3.5 w-3.5 animate-spin"
                                    aria-hidden
                                  />
                                ) : (
                                  <CreditCard className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                                )}
                                Pay now
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Verification alert (desktop) */}
              {verificationResult &&
                pendingInvoices.some((i) => i.id === verificationResult.invoiceId) && (
                  <Alert variant="destructive" className="hidden flex-col gap-3 sm:flex">
                    <div className="flex items-start justify-between gap-3">
                      <AlertDescription className="min-w-0 flex-1 text-sm">
                        <span className="font-medium">
                          Status: {verificationResult.status.toUpperCase()}
                        </span>
                        {" · "}
                        {verificationResult.message}
                        {verificationResult.reference && (
                          <span className="mt-2 block break-all font-mono text-xs">
                            Reference: {verificationResult.reference}
                          </span>
                        )}
                      </AlertDescription>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 shrink-0"
                        onClick={() => setVerificationResult(null)}
                        aria-label="Dismiss"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    {verificationResult.reference && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-fit"
                        onClick={() => {
                          navigator.clipboard.writeText(verificationResult.reference!);
                          toast.success("Reference copied");
                        }}
                      >
                        <Copy className="mr-2 h-3.5 w-3.5" aria-hidden />
                        Copy reference
                      </Button>
                    )}
                  </Alert>
                )}

              {/* Mobile list */}
              <ul className="space-y-3 sm:hidden" role="list">
                {pendingInvoices.map((inv, idx) => {
                  const sn = (pendingPage - 1) * PAGE_SIZE + idx + 1;
                  return (
                    <li
                      key={inv.id}
                      className="overflow-hidden rounded-xl border border-slate-200 bg-white"
                    >
                      <div className="flex items-start justify-between gap-3 px-4 py-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium tabular-nums text-slate-400">
                              {sn}.
                            </span>
                            <p className="font-heading text-sm font-semibold text-slate-900">
                              {invoiceTitle(inv)}
                            </p>
                          </div>
                        </div>
                        <InvoiceStatusBadge status={inv.status} />
                      </div>
                      <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2.5 text-sm">
                        <span className="font-semibold tabular-nums text-slate-900">
                          {CURRENCY_SYMBOL}
                          {tenantInvoiceAmount(inv).toLocaleString()}
                        </span>
                        <span className="text-xs text-slate-500">
                          Due {inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : "—"}
                        </span>
                      </div>
                      <div className="flex gap-2 border-t border-slate-100 px-4 py-2.5">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-10 flex-1 touch-manipulation"
                          onClick={() => handleVerifyPayment(inv)}
                          disabled={verifyingId === inv.id}
                        >
                          {verifyingId === inv.id ? (
                            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
                          ) : (
                            <RefreshCw className="mr-1.5 h-4 w-4" aria-hidden />
                          )}
                          Verify
                        </Button>
                        <Button
                          size="sm"
                          className="h-10 flex-1 touch-manipulation"
                          onClick={() => handlePay(inv)}
                          disabled={payingId === inv.id}
                        >
                          {payingId === inv.id ? (
                            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
                          ) : (
                            <CreditCard className="mr-1.5 h-4 w-4" aria-hidden />
                          )}
                          Pay now
                        </Button>
                      </div>
                      {verificationResult?.invoiceId === inv.id && (
                        <div className="border-t border-slate-100 px-4 py-3 sm:hidden">
                          <Alert variant="destructive" className="flex flex-col gap-2">
                            <AlertDescription className="text-sm">
                              <span className="font-medium">
                                {verificationResult.status.toUpperCase()}
                              </span>
                              {" · "}
                              {verificationResult.message}
                            </AlertDescription>
                            <div className="flex gap-2">
                              {verificationResult.reference && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    navigator.clipboard.writeText(verificationResult.reference!);
                                    toast.success("Reference copied");
                                  }}
                                >
                                  <Copy className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                                  Copy ref
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setVerificationResult(null)}
                              >
                                Dismiss
                              </Button>
                            </div>
                          </Alert>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>

              <Pagination
                mode="cursor"
                currentPage={pendingPage}
                totalPages={pendingTotalPages}
                totalItems={pendingTotal}
                itemsPerPage={PAGE_SIZE}
                hasMore={pendingHasMore}
                loading={pendingLoading}
                onFirst={() => {
                  setPendingStack([]);
                  void loadPending(null);
                }}
                onPrev={() => {
                  if (pendingStack.length === 0) return;
                  const prev = pendingStack[pendingStack.length - 1];
                  setPendingStack((s) => s.slice(0, -1));
                  void loadPending(prev);
                }}
                onNext={() => {
                  if (!pendingNext) return;
                  setPendingStack((s) => [...s, pendingReqCursor]);
                  void loadPending(pendingNext);
                }}
              />
            </>
          )}
        </TabsContent>

        {/* ── Payment History tab ──────────────────────────────── */}
        <TabsContent value="history" className="mt-4 space-y-4">
          {paidLoading && paidInvoices.length === 0 ? (
            <div className="flex min-h-[120px] items-center justify-center py-8" role="status">
              <Loader2 className="h-7 w-7 animate-spin text-slate-400" aria-hidden />
            </div>
          ) : paidInvoices.length === 0 ? (
            <EmptyState
              icon={<Clock className="h-6 w-6 text-slate-400" />}
              title="No payment history yet"
              description="Completed payments and their receipts will show up here."
            />
          ) : (
            <>
              {/* Desktop / tablet table */}
              <div className="hidden overflow-x-auto rounded-lg border border-slate-200 sm:block">
                <table className="w-full min-w-[560px] border-collapse text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50/80">
                      <th className="w-12 px-3 py-3 text-center font-heading text-xs font-semibold uppercase tracking-wide text-slate-500">
                        S/N
                      </th>
                      <th className="px-4 py-3 font-heading text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Invoice
                      </th>
                      <th className="px-4 py-3 text-right font-heading text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Paid
                      </th>
                      <th className="px-4 py-3 font-heading text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Status
                      </th>
                      <th className="px-4 py-3 font-heading text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Date
                      </th>
                      <th className="px-4 py-3 text-right font-heading text-xs font-semibold uppercase tracking-wide text-slate-500">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {paidInvoices.map((inv, idx) => {
                      const sn = (paidPage - 1) * PAGE_SIZE + idx + 1;
                      return (
                        <tr key={inv.id} className="hover:bg-slate-50/60">
                          <td className="whitespace-nowrap px-3 py-3 text-center tabular-nums text-slate-400">
                            {sn}
                          </td>
                          <td className="max-w-[240px] px-4 py-3 align-top">
                            <span className="font-medium text-slate-900">{invoiceTitle(inv)}</span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-right font-medium tabular-nums text-slate-900">
                            {CURRENCY_SYMBOL}
                            {tenantInvoiceAmount(inv).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <InvoiceStatusBadge status={inv.status} />
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-slate-500">
                            {inv.paidAt ? new Date(inv.paidAt).toLocaleDateString() : "—"}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              className="touch-manipulation"
                              onClick={() => handlePrintReceipt(inv.id)}
                            >
                              <Receipt className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                              Receipt
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile list */}
              <ul className="space-y-3 sm:hidden" role="list">
                {paidInvoices.map((inv, idx) => {
                  const sn = (paidPage - 1) * PAGE_SIZE + idx + 1;
                  return (
                    <li
                      key={inv.id}
                      className="overflow-hidden rounded-xl border border-slate-200 bg-white"
                    >
                      <div className="flex items-start justify-between gap-3 px-4 py-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium tabular-nums text-slate-400">
                              {sn}.
                            </span>
                            <p className="font-heading text-sm font-semibold text-slate-900">
                              {invoiceTitle(inv)}
                            </p>
                          </div>
                        </div>
                        <InvoiceStatusBadge status={inv.status} />
                      </div>
                      <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2.5 text-sm">
                        <span className="font-semibold tabular-nums text-slate-900">
                          {CURRENCY_SYMBOL}
                          {tenantInvoiceAmount(inv).toLocaleString()}
                        </span>
                        <span className="text-xs text-slate-500">
                          {inv.paidAt ? new Date(inv.paidAt).toLocaleDateString() : "—"}
                        </span>
                      </div>
                      <div className="border-t border-slate-100 px-4 py-2.5">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-10 w-full touch-manipulation"
                          onClick={() => handlePrintReceipt(inv.id)}
                        >
                          <Receipt className="mr-2 h-4 w-4" aria-hidden />
                          Open receipt
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>

              <Pagination
                mode="cursor"
                currentPage={paidPage}
                totalPages={paidTotalPages}
                totalItems={paidTotal}
                itemsPerPage={PAGE_SIZE}
                hasMore={paidHasMore}
                loading={paidLoading}
                onFirst={() => {
                  setPaidStack([]);
                  void loadPaid(null);
                }}
                onPrev={() => {
                  if (paidStack.length === 0) return;
                  const prev = paidStack[paidStack.length - 1];
                  setPaidStack((s) => s.slice(0, -1));
                  void loadPaid(prev);
                }}
                onNext={() => {
                  if (!paidNext) return;
                  setPaidStack((s) => [...s, paidReqCursor]);
                  void loadPaid(paidNext);
                }}
              />
            </>
          )}
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}
